import { useState } from 'react';
import { Input } from '@/components/atoms/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';

export type MemberPricingMode = 'NONE' | 'PRICE' | 'PERCENT';

export interface LineMemberPricing {
  memberPrice?: number | null;
  memberDiscountPercent?: number | null;
}

/**
 * What the line is priced at, so the control can say what the member actually
 * pays. Read only — `onChange` still patches the editable pair alone.
 */
export interface LineMemberPricingContext {
  /** The pricebook's original. The DENOMINATOR of the member rate. */
  basePrice?: number | null;
  /** The operator's price for this document, when the line carries one. */
  manualPriceOverride?: number | null;
  /** How many. The engine rounds the LINE, not the unit — see below. */
  quantity?: number | null;
}

interface LineMemberPricingFieldsProps {
  value: LineMemberPricing & LineMemberPricingContext;
  onChange: (patch: LineMemberPricing) => void;
}

/**
 * What a member is actually charged for this line, given what it sells for.
 *
 * "Fixed member price" is true only while the line sells at the catalog price.
 * The engine on both sides prices a member at `amount x memberPrice / basePrice`
 * — the catalog member RATE applied to the final amount (SPEC-line-item-model,
 * "Member pricing applies to the final price"). So on a line priced away from
 * the catalog the typed figure is a rate anchor, not the member's bill: catalog
 * $150 with a member price of $80, sold at $200, charges the member $106.67.
 *
 * THE LINE TOTAL, NOT A UNIT PRICE, because the engine rounds once on the whole
 * amount and a per-unit figure does not multiply back. At $200 x 3 on that same
 * rate the engine charges $320.00, while $106.67 a unit reads as $320.01 — a
 * penny the operator would be quoting and nobody would be billing. A line has to
 * SHOW what it CONTRIBUTES.
 *
 * Returns null when the rate resolves to exactly the figure typed, which is
 * every line priced at its catalog price — there is nothing to say, and saying
 * it anyway would train the operator to ignore the line that matters.
 */
function resolvedMemberLineTotal(
  value: LineMemberPricing & LineMemberPricingContext,
): number | null {
  const memberPrice = Number(value.memberPrice);
  const basePrice = Number(value.basePrice);
  if (!Number.isFinite(memberPrice) || !Number.isFinite(basePrice)) return null;
  // The engine only treats `memberPrice` as a rate when it is an actual
  // discount below the catalog price; otherwise the percent fallback applies
  // and this control is not what decides the bill.
  if (!(basePrice > 0) || !(memberPrice < basePrice)) return null;

  const effectivePrice = Number.isFinite(value.manualPriceOverride)
    ? Number(value.manualPriceOverride)
    : basePrice;
  const quantity =
    Number.isFinite(value.quantity) && Number(value.quantity) > 0 ? Number(value.quantity) : 1;

  // Rounded exactly where the engine rounds it: once, on the line.
  const resolved = Math.round(effectivePrice * quantity * (memberPrice / basePrice) * 100) / 100;
  const asTypedWouldBe = Math.round(memberPrice * quantity * 100) / 100;
  return Math.abs(resolved - asTypedWouldBe) < 0.005 ? null : resolved;
}

const money = (amount: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

/**
 * What a member pays for a hand-typed line — the pricebook item form's member
 * pricing control, on a line that has no catalog item to carry it.
 *
 * One MODE, not two independent fields. A fixed member price and a member
 * discount % have opposite precedence against the plan's own discount and
 * cannot both apply, so the two are kept mutually exclusive here rather than
 * left for the pricing engine to arbitrate: with both set, which one wins is a
 * detail of the engine, and the operator has no way to see which they got.
 */
export function LineMemberPricingFields({ value, onChange }: LineMemberPricingFieldsProps) {
  const resolvedMemberTotal = resolvedMemberLineTotal(value);
  const [mode, setMode] = useState<MemberPricingMode>(() => {
    if (value.memberPrice != null) return 'PRICE';
    if (value.memberDiscountPercent != null && value.memberDiscountPercent > 0) return 'PERCENT';
    return 'NONE';
  });

  const selectMode = (next: string) => {
    if (next !== 'NONE' && next !== 'PRICE' && next !== 'PERCENT') return;
    setMode(next);
    // Clearing the mode the operator left is what keeps the two exclusive.
    onChange({
      memberPrice: next === 'PRICE' ? (value.memberPrice ?? null) : null,
      memberDiscountPercent: next === 'PERCENT' ? (value.memberDiscountPercent ?? null) : null,
    });
  };

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="space-y-1.5">
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Member pricing
        </span>
        <Select value={mode} onValueChange={selectMode}>
          <SelectTrigger className="h-9 bg-background text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="NONE">Use plan discount</SelectItem>
            <SelectItem value="PRICE">Fixed member price</SelectItem>
            <SelectItem value="PERCENT">Member discount %</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {mode === 'PRICE' ? (
        <div className="space-y-1.5">
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Member price
          </span>
          <Input
            type="number"
            min={0}
            step={0.01}
            value={value.memberPrice ?? ''}
            onChange={(event) =>
              onChange({
                memberPrice: event.target.value === '' ? null : Number(event.target.value),
              })
            }
            placeholder="0.00"
            className="h-9 bg-background text-xs"
          />
          <p className="text-[11px] leading-tight text-muted-foreground">
            Overrides the plan discount for this line.
          </p>
          {resolvedMemberTotal != null ? (
            <p className="text-[11px] leading-tight text-amber-600 dark:text-amber-500">
              This line is priced away from the catalog, so the member rate applies to what it sells
              for: a member pays <strong>{money(resolvedMemberTotal)}</strong> for this line.
            </p>
          ) : null}
        </div>
      ) : null}

      {mode === 'PERCENT' ? (
        <div className="space-y-1.5">
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Member discount %
          </span>
          <Input
            type="number"
            min={0}
            max={100}
            step={1}
            value={value.memberDiscountPercent ?? ''}
            onChange={(event) =>
              onChange({
                memberDiscountPercent:
                  event.target.value === '' ? null : Number(event.target.value),
              })
            }
            placeholder="e.g. 10"
            className="h-9 bg-background text-xs"
          />
          <p className="text-[11px] leading-tight text-muted-foreground">
            Applies only when the plan gives no discount on this line.
          </p>
        </div>
      ) : null}
    </div>
  );
}
